import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

/* ===========================================================================
   READ THIS BEFORE TREATING THIS COMPONENT AS A SECURITY CONTROL — IT IS NOT.

   This runs in the browser and reads `localStorage`, which the person using the
   browser owns. Anyone can set `isSuperAdmin: true` in devtools and walk
   straight through it. What it actually provides is **navigation correctness**:
   people who are not admins do not land on admin screens by following a link,
   a bookmark or a stale tab.

   The real control has to live on the server, where the caller cannot edit the
   answer — every admin-only read and write must be rejected by the API for a
   non-admin token, regardless of what the UI allowed. That is tracked as SEC-1
   and is NOT closed by this file. This component closes SEC-3 (the same check
   copy-pasted across 11 pages, so no single place to fix) and SEC-2
   (`/integrations` had no check at all).

   Concretely: the admin dashboard's data comes from `wc/v3`, which WooCommerce
   already gates on real capabilities. So a spoofed `isSuperAdmin` gets a broken
   page full of 403s rather than a working dashboard — which is luck arising from
   the API's own permissions, not a guarantee this app provides.
   =========================================================================== */

const readUserData = () => {
    try {
        return JSON.parse(localStorage.getItem('user_data')) || {};
    } catch {
        // Corrupt or unreadable storage is treated as "not signed in" rather than
        // throwing — a parse error must not render an admin screen.
        return {};
    }
};

/**
 * Wraps a route element and redirects instead of rendering when the viewer
 * should not be there.
 *
 * Redirecting during render (via <Navigate>) rather than in a `useEffect` is the
 * other reason this is better than the 11 copies it replaces: those rendered the
 * page first and navigated away afterwards, so the admin screen flashed on
 * screen and its data-fetching effects fired before the redirect landed.
 *
 * @param {boolean} requireSuperAdmin  Admin-only route (the default). Set false
 *                                     for a route that only needs a signed-in user.
 */
function ProtectedRoute({ children, requireSuperAdmin = true }) {
    const location = useLocation();
    const userData = readUserData();

    // `user_data` is the same key the API layer uses to decide whether a real user
    // is signed in (see hasUserSession in Api/axiosInstance.js). Keeping both on one
    // key means a signed-out user cannot be half-signed-in across the two.
    const isSignedIn = Boolean(localStorage.getItem('user_data'));

    if (!isSignedIn) {
        // Carry the attempted path so login can return them to it.
        return <Navigate to="/login" replace state={{ from: location.pathname }} />;
    }

    if (requireSuperAdmin && !userData.isSuperAdmin) {
        // Signed in but not an admin: home, matching the previous behaviour.
        return <Navigate to="/" replace />;
    }

    return children;
}

export default ProtectedRoute;
